import { useEffect, useRef, useState } from "react";

import "../../shimmer.css";

import SearchSidebar from "./SearchSidebar";

// icons
import CloseIcon from "../icons/Close";

const DISMISS_DISTANCE = 110; // px dragged past which we dismiss
const DISMISS_VELOCITY = 0.5; // px/ms flick speed that dismisses regardless of distance
const INTENT_THRESHOLD = 5; // px before we commit to scroll-vs-drag

const SearchSidebarPhone = (props) => {
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [closing, setClosing] = useState(false);

  // refs hold the live gesture values so onTouchEnd never reads stale state
  const startYRef = useRef(null);
  const startTimeRef = useRef(0);
  const offsetRef = useRef(0);
  const closedRef = useRef(false);
  const modeRef = useRef(null); // null | "sheet" | "scroll"
  const fromContentRef = useRef(false);
  const contentRef = useRef(null);

  // lock background scroll while the sheet is mounted
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // single, idempotent close (guards against double-fire from drag + timeout)
  const close = () => {
    if (closedRef.current) return;
    closedRef.current = true;
    props.onClick();
  };

  // animate the sheet down, then unmount (with a safety net if transitionend never fires)
  const animateDismiss = () => {
    setIsDragging(false);
    setClosing(true);
    setTimeout(close, 400);
  };

  const resetDrag = () => {
    startYRef.current = null;
    offsetRef.current = 0;
    modeRef.current = null;
    setIsDragging(false);
    setDragOffset(0);
  };

  const onTouchStart = (event) => {
    if (event.touches.length !== 1 || closing) return;
    startYRef.current = event.touches[0].clientY;
    startTimeRef.current = Date.now();
    offsetRef.current = 0;
    modeRef.current = null;
    // a touch that begins inside the scroll area may belong to the scroll
    fromContentRef.current =
      !!contentRef.current && contentRef.current.contains(event.target);
  };

  const onTouchMove = (event) => {
    if (startYRef.current === null || event.touches.length !== 1) return;
    const delta = event.touches[0].clientY - startYRef.current;

    // decide once whether this gesture drags the sheet or scrolls the content
    if (modeRef.current === null) {
      if (Math.abs(delta) < INTENT_THRESHOLD) return;
      const atTop = !contentRef.current || contentRef.current.scrollTop <= 0;
      // from the header: always draggable. from content: only when at the top.
      const canDragSheet = fromContentRef.current ? delta > 0 && atTop : delta > 0;
      modeRef.current = canDragSheet ? "sheet" : "scroll";
      if (modeRef.current === "sheet") setIsDragging(true);
    }

    if (modeRef.current !== "sheet") return;

    const next = delta > 0 ? delta : 0; // downward only
    offsetRef.current = next;
    setDragOffset(next);
  };

  const onTouchEnd = () => {
    if (startYRef.current === null) return;
    const wasSheet = modeRef.current === "sheet";
    const elapsed = Math.max(Date.now() - startTimeRef.current, 1);
    const velocity = offsetRef.current / elapsed;

    if (
      wasSheet &&
      (offsetRef.current > DISMISS_DISTANCE || velocity > DISMISS_VELOCITY)
    ) {
      startYRef.current = null;
      modeRef.current = null;
      animateDismiss();
    } else {
      resetDrag();
    }
  };

  const onSheetTransitionEnd = (event) => {
    if (closing && event.propertyName === "transform") close();
  };

  const sheetTransform = closing
    ? "translateY(100%)"
    : dragOffset
    ? `translateY(${dragOffset}px)`
    : undefined;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search tenders"
      className="lg:hidden fixed inset-0 z-[101] flex items-end justify-center"
    >
      {/* dimmed backdrop */}
      <div
        onClick={close}
        className={`absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300 ${
          closing ? "opacity-0" : "opacity-100 animate-sheet-fade"
        }`}
      />

      {/* bottom sheet — drag works from anywhere on the sheet */}
      <div
        onTransitionEnd={onSheetTransitionEnd}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={resetDrag}
        style={{
          transform: sheetTransform,
          transition: isDragging
            ? "none"
            : "transform 0.34s cubic-bezier(0.32, 0.72, 0, 1)",
        }}
        className={`relative w-full max-w-[500px] max-h-[90vh] flex flex-col bg-white rounded-t-[28px] shadow-[0_-8px_40px_rgba(0,0,0,0.18)] overflow-hidden ${
          closing ? "" : "animate-sheet-up"
        }`}
      >
        {/* header */}
        <div className="shrink-0 px-5 pt-4 pb-3 flex items-center justify-between select-none">
          <h1 className="text-lg font-bold text-[#664bc7]">Search Tenders</h1>
          <button
            onClick={close}
            aria-label="Close"
            className="p-1.5 flex items-center justify-center rounded-full bg-white/50 backdrop-blur-md ring-1 ring-black/10 shadow-sm hover:bg-white/80 transition-colors"
          >
            <CloseIcon styles="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* scrollable content (drag-to-dismiss when scrolled to top) */}
        <div
          ref={contentRef}
          className="flex-1 overflow-y-auto overscroll-contain px-5 pb-[calc(env(safe-area-inset-bottom)+24px)]"
        >
          <SearchSidebar redirect={true} filter={props.filter} />
        </div>
      </div>
    </div>
  );
};

export default SearchSidebarPhone;
